// src/services/baslikDefault.js
//
// Kullanıcının hesabına bağlı "varsayılan başlık şablonu" yönetimi.
// - Varsayılan ID backend'de tutulur (GET/PUT /api/me/settings).
// - Proje kendi başlığını taşımıyorsa (activeTemplateId yok) varsayılan uygulanır.

import API_URL from '../config';
import { store } from '../redux/store';
import { activateBackendTemplate } from '../redux/baslikTemplateSlice';

const token = () => localStorage.getItem('projabze_token');

/** Kullanıcının varsayılan başlık şablonu ID'sini döndürür (yoksa null). */
export async function fetchDefaultBaslikId() {
  const t = token();
  if (!t) return null;
  try {
    const res = await fetch(`${API_URL}/api/me/settings`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.defaultBaslikTemplateId || null;
  } catch (_) {
    return null;
  }
}

/** Kullanıcının tüm başlık (cad_template, subType=baslik) şablonlarını listeler. */
export async function fetchBaslikTemplateList() {
  const t = token();
  if (!t) return [];
  try {
    const res = await fetch(`${API_URL}/api/documents?type=cad_template`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data?.documents) ? data.documents : (Array.isArray(data) ? data : []);
    return list
      .filter(d => !d.subType || d.subType === 'baslik')
      .map(d => ({ id: d.id, name: d.title || 'Şablon' }));
  } catch (_) {
    return [];
  }
}

/** Verilen şablonu kullanıcının varsayılanı olarak kaydeder (null = temizle). */
export async function saveDefaultBaslikId(id) {
  const t = token();
  if (!t) throw new Error('Oturum bulunamadı');
  const res = await fetch(`${API_URL}/api/me/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
    body: JSON.stringify({ defaultBaslikTemplateId: id || null }),
  });
  if (!res.ok) throw new Error('Varsayılan kaydedilemedi');
  const data = await res.json();
  return data.defaultBaslikTemplateId || null;
}

/**
 * Bir başlık şablonunu backend'den çekip aktif eder (DocumentsPopup ile aynı akış).
 * @returns {Promise<boolean>} başarı
 */
export async function applyBaslikTemplate(templateId) {
  const t = token();
  if (!t || !templateId) return false;
  try {
    const detailRes = await fetch(`${API_URL}/api/documents/${templateId}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!detailRes.ok) return false;
    const detail = await detailRes.json();
    const doc = detail.document || detail;
    const payload = doc.payload || {};
    let dxfRawText = '';
    if (doc.fileUrl) {
      const dxfRes = await fetch(`${API_URL}${doc.fileUrl}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (dxfRes.ok) dxfRawText = await dxfRes.text();
    }
    if (!dxfRawText) return false;
    store.dispatch(activateBackendTemplate({
      id: doc.id,
      name: doc.title || 'Şablon',
      subType: doc.subType || payload.subType || 'baslik',
      dxfRawText,
      bindings: payload.bindings || {},
      marka: payload.marka || null,   // native marka alanı ayarları (ole/native/circle/square/data)
      notesPayload: null,
      docId: doc.id,
    }));
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Proje kendi başlığını taşımıyorsa kullanıcının varsayılan başlığını uygular.
 * Proje yüklendikten (hydrate) sonra çağrılır. Kendi başlığı olan projeleri
 * etkilemez (activeTemplateId doluysa erken döner).
 */
export async function ensureDefaultBaslik() {
  try {
    if (store.getState().baslikTemplate?.activeTemplateId) return false;
    const defId = await fetchDefaultBaslikId();
    if (!defId) return false;
    // Yarış koruması: bu arada bir başlık atanmış olabilir.
    if (store.getState().baslikTemplate?.activeTemplateId) return false;
    return await applyBaslikTemplate(defId);
  } catch (_) {
    return false;
  }
}
